import { contract, core, debt as debtNs, formatDay, parseDay, schedule, type Day, type Transaction } from '@map-my-money/engine';
import type { WirePlan } from './run.ts';

export interface ItemEnd {
  /** The item's last payment. */
  readonly last: string;
  /** Where its next payment would have fallen: a redirect's first payment. */
  readonly next: string;
}

function lastPayment(t: Transaction, today: Day): Day | null {
  if (t.type === 'debt') return debtNs.payoffDate(t, today, today);
  if (t.frequency === 'oneTime') return t.startDate;
  if (t.endAfterOccurrences !== null) return t.endAfterOccurrences > 0 ? schedule.occurrenceDate(t, t.endAfterOccurrences - 1) : null;
  if (t.endDate !== null) {
    const count = schedule.countOccurrencesUpTo(t, t.endDate);
    return count > 0 ? schedule.occurrenceDate(t, count - 1) : null;
  }
  return null;
}

/**
 * When a transaction makes its last payment, by the engine on `today` (a debt's payoff, an item's
 * end date or last occurrence), and when its next would have been; null when it never ends. A
 * what-if dated "when X ends" reads this when it runs, so it moves when X changes.
 */
export function itemEnd(plan: WirePlan, id: string, today: string): ItemEnd | null {
  const t = contract.decodePlan(plan).transactions.find((x) => x.id === id);
  if (!t) return null;
  const last = lastPayment(t, parseDay(today));
  if (last === null) return null;
  const step = schedule.frequencyStep(t.frequency) ?? { unit: 'month' as const, count: 1 };
  const next = t.type === 'debt' ? core.addMonths(last, 1) : schedule.scheduleDate(last, step, 1);
  return { last: formatDay(last), next: formatDay(next) };
}
